// src/pages/products/SpendNotePage.jsx
import { motion } from "framer-motion";

// Assets
import spendIcon from "../../assets/product-icons/spendnote.png";
import screenshot1 from "../../assets/products/spendnote-1.png";
import screenshot2 from "../../assets/products/spendnote-2.png";
import screenshot3 from "../../assets/products/spendnote-3.png";
import screenshot4 from "../../assets/products/spendnote-3.png";
// Icons
import { FaApple, FaAndroid } from "react-icons/fa";

export default function SpendNotePage() {
  
  const screenshots = [
    screenshot1,
    screenshot2,
    screenshot3,
    screenshot4, // add/remove freely
  ];
  return (
    
    <div className="w-full bg-transparent text-black pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6 md:px-20">

        {/* ================= HERO ================= */}
        <section className="flex flex-col items-center gap-10 text-center">

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="hero-heading mb-4">
              SpendNote
            </h1>

            <p className="hero-copy">
              A fast, clutter-free expense tracking app designed to help you
              stay in control of your money without ads, noise, or complexity.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="flex justify-center"
          >
            <img
              src={spendIcon}
              alt="SpendNote App"
              className="w-64 h-64 object-contain drop-shadow-2xl"
            />
          </motion.div>
        </section>

        {/* ================= OVERVIEW ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-6">
            Overview
          </h2>

          <p className="section-copy">
            SpendNote is built for people who want to track expenses without
            turning it into a task. Unlike traditional finance apps packed with
            ads, charts, and distractions, SpendNote focuses on speed, clarity,
            and everyday usability.
          </p>

          <p className="section-copy mt-6">
            Whether you’re managing personal expenses, business spending, or both,
            SpendNote helps you capture transactions instantly, organize them
            automatically, and understand your money with minimal effort.
          </p>
        </section>

        {/* ================= WHY SPENDNOTE ================= */}
        <section className="mx-auto mt-32 max-w-6xl text-center">
          <h2 className="section-heading mb-10">
            Why SpendNote
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Add expenses in seconds with a distraction free flow",
              "Track personal and business expenses in one app",
              "Store receipts and invoices alongside transactions",
              "Built in budgeting to stay within limits",
              "Clear insights without overwhelming charts",
              "Designed for daily use, not financial experts",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <p className="text-[15px] font-medium">{item}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= CORE FEATURES ================= */}
        <section className="mt-32">
          <h2 className="section-heading mb-10">
            Core Features
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[
              "Instant expense entry",
              "Smart auto categorization",
              "Personal & business modes",
              "Receipt & invoice storage",
              "Offline first performance",
              "Secure local data handling",
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className="
                  glass-card rounded-[22px] p-6
                "
              >
                <h3 className="text-[15px] font-medium">{item}</h3>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= SCREENSHOTS ================= */}
  <section className="mt-32">
  <motion.h2
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6 }}
    className="section-heading mb-10"
  >
    App Preview
  </motion.h2>

  <div className="relative overflow-hidden">
    <motion.div
      className="flex gap-5 w-max"
      animate={
        screenshots.length > 3
          ? { x: ["0%", "-50%"] }
          : {}
      }
      transition={{
        duration: 25,
        ease: "linear",
        repeat: Infinity,
      }}
    >
      {[...screenshots, ...screenshots].map((img, i) => (
        <motion.img
          key={i}
          src={img}
          alt="SpendNote Screenshot"
          whileHover={{ y: -6, scale: 1.04 }}
          transition={{ duration: 0.3 }}
          className="
            w-[200px] md:w-[240px]
            rounded-xl
            border border-black/10
            shadow-lg
            shrink-0
          "
        />
      ))}
    </motion.div>
  </div>
</section>

        {/* ================= HOW IT WORKS ================= */}
        <section className="mx-auto mt-32 max-w-5xl text-center">
          <h2 className="section-heading mb-10">
            How It Works
          </h2>

          <div className="space-y-12">
            {[
              { step: "01", title: "Add Spending", desc: "Quick entry designed for speed and ease." },
              { step: "02", title: "Attach Receipts", desc: "Store invoices and bills with each expense." },
              { step: "03", title: "Auto Categorization", desc: "Expenses are grouped automatically." },
              { step: "04", title: "Track Budgets", desc: "Set limits and stay aware of spending." },
              { step: "05", title: "Understand Insights", desc: "See clear patterns without complexity." },
            ].map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className="glass-card flex gap-6 items-start rounded-[22px] p-5"
              >
                <h3 className="text-4xl font-semibold text-black">{p.step}</h3>
                <div>
                  <h4 className="text-xl font-semibold mb-1">{p.title}</h4>
                  <p className="text-black/60 text-[15px] leading-[1.6]">
                    {p.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* ================= LAUNCH ================= */}
        <section className="mt-32 text-center">
  <h2 className="section-heading mb-4">
    Launching Soon On
  </h2>

  <div className="flex justify-center gap-8 mt-10 flex-wrap">

    {/* iOS */}
    <div
  className="glass-card flex h-28 w-40 flex-col items-center justify-center rounded-2xl"
>
  {/* ICON — no inner box */}
  <FaApple className="text-[28px] text-black/80 mb-3" />

  <span className="text-[14px] font-medium tracking-wide">
    iOS
  </span>
</div>

    {/* Android */}
    <div
  className="glass-card flex h-28 w-40 flex-col items-center justify-center rounded-2xl"
>
  <FaAndroid className="text-[28px] text-black/80 mb-3" />

  <span className="text-[14px] font-medium tracking-wide">
    Android
  </span>
</div>

  </div>

  {/* CTA */}
  <a
    href="/contact"
    className="
      inline-block mt-12 px-10 py-3 rounded-full
      bg-black text-white font-medium
      hover:bg-opacity-80 transition
    "
  >
    Get Early Access
  </a>
</section>

      </div>
    </div>
  );
}
